import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LogIn, Loader2, Image as ImageIcon, X, ChevronLeft, ChevronRight, Play, Pause, Images, Film, Mail, Send, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import emproLogo from "@/assets/empro-logo.png";
import ShopLayout from "@/pages/shop/ShopLayout";

export default function PublicGallery({ embedded = false }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const group = searchParams.get("grupa") || "";
  const [items, setItems] = useState([]);
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState("all");
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ garment_type: "", quantity: "", contact: "", name: "", message: "" });
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submitQuote = async () => {
    if (!form.garment_type || !form.contact.trim()) return toast.error("Podaj typ odzieży i kontakt (e-mail lub telefon)");
    setSending(true);
    try {
      const { data } = await api.post("/public/quote", form);
      if (data.ok) { toast.success("Dziękujemy! Zapytanie zostało wysłane — odezwiemy się wkrótce."); setQuoteOpen(false); setForm({ garment_type: "", quantity: "", contact: "", name: "", message: "" }); }
      else toast.error(data.message || "Nie udało się wysłać zapytania.");
    } catch { toast.error("Nie udało się wysłać zapytania. Spróbuj ponownie."); }
    finally { setSending(false); }
  };

  const close = useCallback(() => { setLightbox(null); setPlaying(false); }, []);
  const next = useCallback(() => setLightbox((i) => (i === null ? i : (i + 1) % items.length)), [items.length]);
  const prev = useCallback(() => setLightbox((i) => (i === null ? i : (i - 1 + items.length) % items.length)), [items.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => { if (e.key === "Escape") close(); else if (e.key === "ArrowRight") next(); else if (e.key === "ArrowLeft") prev(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, close, next, prev]);

  useEffect(() => {
    if (lightbox === null || !playing || items.length < 2) return;
    if (items[lightbox]?.media_type === "video") return;
    const t = setInterval(() => next(), 3000);
    return () => clearInterval(t);
  }, [lightbox, playing, next, items]);

  const load = useCallback((m) => {
    setLoading(true);
    const params = {};
    if (m && m !== "all") params.method = m;
    if (group) params.group = group;
    api.get("/public/realizations", { params })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [group]);
  useEffect(() => { load(method); }, [load]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/public/realizations/methods").then((r) => setMethods(r.data || [])).catch(() => {}); }, []);
  const pick = (m) => { setMethod(m); load(m); };

  const videos = items.filter((it) => it.media_type === "video");
  const images = items.filter((it) => it.media_type !== "video");
  const idxOf = (it) => items.findIndex((x) => x.id === it.id);

  const Shell = embedded ? ShopLayout : "div";
  const shellProps = embedded ? {} : { className: "min-h-screen bg-[#0D1117] text-white", "data-testid": "public-gallery" };
  return (
    <Shell {...shellProps}>
      {!embedded && (
        <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0D1117]/90 backdrop-blur">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
            <img src={emproLogo} alt="EMPRO WORKWEAR" className="h-10 sm:h-12 w-auto object-contain" />
            <div className="flex items-center gap-2">
              <Button data-testid="public-shop-button" onClick={() => navigate("/sklep")} variant="outline" className="gap-2 border-white/20 text-white hover:bg-white/10 font-bold"><ShoppingBag className="h-4 w-4" /><span className="hidden sm:inline">Sklep</span></Button>
              <Button data-testid="public-login-button" onClick={() => navigate("/sklep/logowanie")} className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold"><LogIn className="h-4 w-4" />Zaloguj</Button>
            </div>
          </div>
        </header>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight">Nasze <span className="text-amber-400">realizacje</span></h1>
            <p className="mt-3 text-white/60 max-w-2xl">Profesjonalna odzież robocza z nadrukiem. Zobacz wybrane realizacje wykonane przez EMPRO WORKWEAR.</p>
          </div>
          <Button data-testid="public-quote-header" onClick={() => setQuoteOpen(true)} variant="outline" className="shrink-0 gap-2 border-amber-400/60 text-amber-400 hover:bg-amber-400/10 font-bold"><Mail className="h-4 w-4" />Zapytaj o wycenę</Button>
        </div>

        {group && (
          <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="public-group-active">
            <span className="rounded-full bg-amber-400 px-4 py-1.5 text-sm font-bold text-[#0D1117]">Grupa: {group}</span>
            <button onClick={() => setSearchParams({})} data-testid="public-group-clear" className="rounded-full border border-white/20 px-3 py-1.5 text-sm text-white/70 hover:border-amber-400/60 transition-colors">× Pokaż wszystkie</button>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="public-method-filters">
          <button onClick={() => pick("all")} data-testid="public-method-all" className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${method === "all" ? "bg-amber-400 text-[#0D1117]" : "border border-white/20 text-white/70 hover:border-amber-400/60"}`}>Wszystkie</button>
          {methods.map((m) => (
            <button key={m} onClick={() => pick(m)} data-testid={`public-method-${m}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${method === m ? "bg-amber-400 text-[#0D1117]" : "border border-white/20 text-white/70 hover:border-amber-400/60"}`}>{m}</button>
          ))}
          {items.length > 0 && (
            <button onClick={() => { setLightbox(0); setPlaying(true); }} data-testid="public-slideshow" className="ml-auto inline-flex items-center gap-2 rounded-full bg-amber-400 px-4 py-1.5 text-sm font-bold text-[#0D1117] hover:bg-amber-500 transition-colors"><Images className="h-4 w-4" />Pokaz slajdów</button>
          )}
        </div>

        {!loading && videos.length === 1 && (
          <section data-testid="public-video-hero" className="mb-10">
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-black">
              <video src={`${API}/public/photos/${videos[0].id}`} poster={videos[0].cover ? `${API}/public/videos/${videos[0].id}/cover` : undefined} autoPlay muted loop playsInline controls className="w-full max-h-[70vh] object-contain bg-black" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-5 sm:p-8">
                <p className="font-mono text-xs uppercase tracking-widest text-amber-400">Realizacja wideo</p>
                <h2 className="font-display text-2xl sm:text-4xl font-extrabold uppercase">{videos[0].type_label || "Zobacz nas w akcji"}</h2>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button type="button" onClick={() => setQuoteOpen(true)} data-testid="public-quote-cta" className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-6 py-2.5 text-sm font-bold text-[#0D1117] hover:bg-amber-500 transition-colors"><Mail className="h-4 w-4" />Zapytaj o wycenę</button>
              <span className="text-white/50 text-sm">Napisz do nas — przygotujemy wycenę Twojej odzieży z nadrukiem.</span>
            </div>
          </section>
        )}
        {!loading && videos.length > 1 && (
          <section data-testid="public-video-carousel" className="mb-10">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">Realizacje <span className="text-amber-400">wideo</span></h2>
              <button type="button" onClick={() => setQuoteOpen(true)} data-testid="public-quote-cta" className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2 text-sm font-bold text-[#0D1117] hover:bg-amber-500 transition-colors"><Mail className="h-4 w-4" />Zapytaj o wycenę</button>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 snap-x">
              {videos.map((v) => (
                <button key={v.id} onClick={() => { setLightbox(idxOf(v)); setPlaying(true); }} data-testid={`public-video-${v.id}`} className="group snap-start shrink-0 w-64 sm:w-80 text-left">
                  <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black/50">
                    {v.cover ? <img src={`${API}/public/videos/${v.id}/cover`} alt={v.type_label || "Realizacja wideo"} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center"><Film className="h-8 w-8 text-white/40" /></div>}
                    <span className="absolute inset-0 flex items-center justify-center"><span className="rounded-full bg-black/50 p-3 transition-transform group-hover:scale-110"><Play className="h-6 w-6 text-white" fill="white" /></span></span>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="text-sm font-bold truncate">{v.type_label || "Realizacja wideo"}</span>
                    {v.branding && <span className="shrink-0 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">{v.branding}</span>}
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-amber-400" /></div>
        ) : items.length === 0 ? (
          <div data-testid="public-empty" className="flex flex-col items-center gap-3 py-20 text-white/40"><ImageIcon className="h-10 w-10" /><p className="text-sm">Brak realizacji w tej kategorii.</p></div>
        ) : images.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="public-grid">
            {images.map((it) => (
              <button key={it.id} onClick={() => { setLightbox(idxOf(it)); setPlaying(true); }} data-testid={`public-item-${it.id}`} className="group block text-left rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03] cursor-pointer hover:border-amber-400/60 transition-colors">
                <div className="aspect-square bg-black/40 overflow-hidden relative">
                  <img src={`${API}/public/photos/${it.id}`} alt={it.type_label || "Realizacja"} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                  <span className="text-sm font-bold truncate">{it.type_label || "Realizacja"}</span>
                  {it.branding && <span className="shrink-0 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">{it.branding}</span>}
                </div>
              </button>
            ))}
          </div>
        ) : null}

        {!loading && items.length > 0 && (
          <p data-testid="public-disclaimer" className="mt-10 max-w-3xl text-[11px] leading-relaxed text-white/30">
            Wszystkie prezentowane produkty i nadruki stanowią rzeczywiste realizacje EMPRO WORKWEAR. Część fotografii została poddana obróbce cyfrowej, w tym zmianie tła i aranżacji scenerii, w celu atrakcyjniejszej prezentacji produktów. Przedstawione wzory nadruków odpowiadają naszym wykonanym realizacjom.
          </p>
        )}
      </main>

      {!embedded && (
        <footer className="border-t border-white/10 py-8 text-center text-white/40 text-xs">
          <p>EMPRO WORKWEAR · Profesjonalna odzież robocza z nadrukiem · www.emproworkwear.com</p>
        </footer>
      )}

      {lightbox !== null && items[lightbox] && (
        <div data-testid="public-lightbox" className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur" onClick={close}>
          <button data-testid="lightbox-close" onClick={close} className="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"><X className="h-6 w-6" /></button>
          <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-3 sm:left-6 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20"><ChevronLeft className="h-7 w-7" /></button>
          <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-3 sm:right-6 rounded-full bg-white/10 p-2.5 text-white hover:bg-white/20"><ChevronRight className="h-7 w-7" /></button>
          <div className="max-w-[92vw] max-h-[88vh] flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            {items[lightbox].media_type === "video" ? (
              <video key={items[lightbox].id} src={`${API}/public/photos/${items[lightbox].id}`} controls autoPlay playsInline onEnded={() => playing && next()} className="max-w-full max-h-[78vh] object-contain rounded-xl bg-black" />
            ) : (
              <img src={`${API}/public/photos/${items[lightbox].id}`} alt={items[lightbox].type_label || "Realizacja"} className="max-w-full max-h-[78vh] object-contain rounded-xl" />
            )}
            <div className="flex items-center gap-4 text-white">
              <span className="text-sm font-bold">{items[lightbox].type_label || "Realizacja"}{items[lightbox].branding ? ` · ${items[lightbox].branding}` : ""}</span>
              <span className="text-white/50 text-xs font-mono">{lightbox + 1} / {items.length}</span>
              <button data-testid="lightbox-play" onClick={() => setPlaying((p) => !p)} className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-3 py-1 text-xs font-bold text-[#0D1117] hover:bg-amber-500">{playing ? <><Pause className="h-3.5 w-3.5" />Pauza</> : <><Play className="h-3.5 w-3.5" />Odtwórz</>}</button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent data-testid="quote-dialog" className="max-w-md bg-[#11161d] text-white border-white/10">
          <DialogHeader>
            <DialogTitle className="font-extrabold uppercase">Zapytaj o wycenę</DialogTitle>
            <DialogDescription className="text-white/50 text-xs">Podaj kilka szczegółów — przygotujemy wycenę i odezwiemy się do Ciebie.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-white/70">Typ odzieży *</Label>
              <Select value={form.garment_type} onValueChange={(v) => setF("garment_type", v)}>
                <SelectTrigger data-testid="quote-garment" className="bg-white/5 border-white/15"><SelectValue placeholder="Wybierz typ…" /></SelectTrigger>
                <SelectContent>{["T-shirt", "Polo", "Bluza", "Softshell", "Kurtka robocza", "Spodnie robocze", "Kamizelka", "Czapka", "Inne"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs text-white/70">Ilość (szt.)</Label><Input data-testid="quote-quantity" value={form.quantity} onChange={(e) => setF("quantity", e.target.value)} placeholder="np. 50" inputMode="numeric" className="bg-white/5 border-white/15" /></div>
            <div><Label className="text-xs text-white/70">Twój e-mail lub telefon *</Label><Input data-testid="quote-contact" value={form.contact} onChange={(e) => setF("contact", e.target.value)} placeholder="np. jan@firma.pl lub 600 100 200" className="bg-white/5 border-white/15" /></div>
            <div><Label className="text-xs text-white/70">Imię / firma</Label><Input data-testid="quote-name" value={form.name} onChange={(e) => setF("name", e.target.value)} placeholder="opcjonalnie" className="bg-white/5 border-white/15" /></div>
            <div><Label className="text-xs text-white/70">Wiadomość</Label><textarea data-testid="quote-message" value={form.message} onChange={(e) => setF("message", e.target.value)} rows={3} placeholder="np. nadruk z logo na froncie, kolor granatowy…" className="w-full rounded-md bg-white/5 border border-white/15 px-3 py-2 text-sm resize-y" /></div>
            <Button data-testid="quote-submit" onClick={submitQuote} disabled={sending} className="w-full gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Wyślij zapytanie</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
